import './note.create.form.scss';

import { Box, Collapse } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { FormTextField, NewConvergeButton } from '../../presentation';
import {
  NoteInput,
  NoteTargetName,
  NoteTargetType,
  useUpsertNoteMutation,
} from '@converge/graphql';

import { Stack } from '@mui/system';
import { useConvergeSnackBar } from '../../contexts';
import { normalizeNotes } from '@converge/utils';

interface INoteCreateFormProps extends Partial<NoteInput> {
  showBtn?: boolean | undefined;
  onClose?: () => void;
  callHandleUpdateRenewalType?: () => void;
  disabled?: boolean;
  LabelForConfirmBtn?: string;
}

export const NoteCreateForm = ({
  notes = '',
  targetArea,
  targetType = NoteTargetType.Relationship,
  targetName = NoteTargetName.HasCoverageType,
  targetUuid,
  showBtn = false,
  onClose,
  callHandleUpdateRenewalType,
  disabled,
  targetSubUuid,
  LabelForConfirmBtn,
  ...props
}: INoteCreateFormProps) => {
  const { notify } = useConvergeSnackBar();

  const { upsertNote, loading } = useUpsertNoteMutation({
    onError: (error) =>
      notify(`Failed to add note: ${error.message}`, {
        variant: 'error',
      }),
    onCompleted: () => {
      notify('Note has been added', {
        variant: 'success',
      });
    },
  });
  const formMethods = useForm<NoteInput>({
    defaultValues: {
      notes: normalizeNotes(notes || ''),
      targetArea,
      targetType: targetArea ? undefined : targetType,
      targetUuid: targetUuid,
      targetName: targetArea ? undefined : targetName,
      targetSubUuid,
      ...props,
    },
    shouldUseNativeValidation: false,
  });

  const formNotes = formMethods.watch('notes');
  const submitNoteLabel = showBtn ? 'Save change' : 'Add note';
  return (
    <>
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(async (details) => {
            const sanitizedDetails = {
              ...details,
              notes: normalizeNotes(details.notes || ''),
            };
            await upsertNote({ variables: { details: sanitizedDetails } });
            formMethods.setValue('notes', '');
            callHandleUpdateRenewalType && callHandleUpdateRenewalType();
            onClose && onClose();
          })}
        >
          <Collapse in={!loading} unmountOnExit>
            <Stack marginBottom={2}>
              <FormTextField
                disabled={disabled}
                isRichText
                hiddenLabel
                name="notes"
                label="Notes"
                multiline
                rows={4}
                containerWidth={12}
              />
            </Stack>
          </Collapse>
          <Box className="add-note-btn-container">
            {showBtn && (
              <Box style={{ marginBottom: '30px', width: '120px' }}>
                <NewConvergeButton
                  variant="outlined"
                  onClick={() => {
                    onClose && onClose();
                  }}
                  label="cancel"
                />
              </Box>
            )}
            <Box
              style={{
                marginBottom: '30px',
                width: LabelForConfirmBtn ? '70px' : '140px',
              }}
            >
              <NewConvergeButton
                label={
                  LabelForConfirmBtn ? LabelForConfirmBtn : submitNoteLabel
                }
                type="submit"
                disabled={!formNotes}
                loading={loading}
              />
            </Box>
          </Box>
        </form>
      </FormProvider>
    </>
  );
};
